import { type ConvErrorSeverity, toConvSeverity } from './errorModel.js';

/**
 * FUENTE ÚNICA del estilo visual por severidad de una corrección de gramática.
 *
 * Antes esto vivía duplicado: como objeto en `ErrorDetail.tsx`, como clases CSS
 * en `conversation.css` (subrayado) y NO existía en el debrief — que hardcodeaba
 * rojo para todo, ignorando la severidad. Resultado: en el destill una
 * `suggestion` (celeste = "más natural") se mostraba roja como un error grave.
 *
 * Ahora el render vivo (`ErrorDetail`), el historial (lo reusa) y el debrief
 * (`DebriefFeatured` + `DebriefAccordion`) consumen ESTE mapa, así el usuario
 * aprende el código de color una sola vez. La paleta usa CUATRO matices
 * realmente distintos (antes eran tres tonos de rojo casi idénticos que se
 * percibían como uno solo): rojo intenso = grave, ámbar = gramática, amarillo =
 * detalle, celeste = sugerencia de naturalidad (no es error). El `tag` textual
 * acompaña al color en todas las superficies para que la distinción no dependa
 * sólo del matiz.
 *
 * El subrayado inline sigue en `conversation.css` (`.err-frag.err-sev-*`) porque
 * CSS no puede importar JS; esos hex DEBEN coincidir con los de acá. Si tocás un
 * color, actualizá ambos.
 */
export interface GramSeverityStyle {
  /** Color de acento (texto del tag, dot, ícono). */
  accent: string;
  /** Fondo tenue de la tarjeta. */
  tint: string;
  /** Borde de la tarjeta. */
  border: string;
  /** Etiqueta corta que comunica la gravedad. */
  tag: string;
  /** Rótulo de la forma corregida ("Mejor así" vs "Más natural"). */
  betterLabel: string;
  /** Si se tacha lo que dijo el usuario (un error sí, una sugerencia no). */
  strike: boolean;
}

export const GRAM_SEVERITY_STYLE: Record<ConvErrorSeverity, GramSeverityStyle> = {
  'communication-breaking': {
    accent: '#ef4444', tint: 'rgba(239,68,68,0.08)', border: 'rgba(239,68,68,0.22)',
    tag: 'ERROR GRAVE', betterLabel: 'Mejor así', strike: true,
  },
  structural: {
    accent: '#f59e0b', tint: 'rgba(245,158,11,0.10)', border: 'rgba(245,158,11,0.24)',
    tag: 'GRAMÁTICA', betterLabel: 'Mejor así', strike: true,
  },
  minor: {
    accent: '#eab308', tint: 'rgba(234,179,8,0.10)', border: 'rgba(234,179,8,0.24)',
    tag: 'DETALLE', betterLabel: 'Mejor así', strike: true,
  },
  suggestion: {
    accent: '#60a5fa', tint: 'rgba(96,165,250,0.08)', border: 'rgba(96,165,250,0.22)',
    tag: 'MÁS NATURAL', betterLabel: 'Más natural', strike: false,
  },
};

/**
 * Estilo por severidad, normalizando un valor de origen desconocido (string del
 * backend, posible `null`). Default `structural` (fail-safe), igual que
 * `toConvSeverity`. Usalo cuando el severity no viene tipado como
 * `ConvErrorSeverity` (p. ej. la data persistida del debrief).
 */
export function gramSeverityStyle(s: string | null | undefined): GramSeverityStyle {
  return GRAM_SEVERITY_STYLE[toConvSeverity(s)];
}
